import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Info } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { apiErrorMessage } from 'utils/errorMessage';
import { useDeclareUnavailability } from 'hooks/useUnavailabilities';
import { formatPeriod } from 'utils/unavailability';

/**
 * The declaration itself, without its container: the profile screen wraps it in a sheet of its
 * own, the ticket screen shows it as the second view of the sheet it already has. Two dates and
 * an optional reason. No recurrence and no half-day (J1).
 *
 * It owns the two panes — the form and the confirmation — because they share the mutation and
 * the reset. What differs between hosts is only where "done" leads, which is why that is a prop.
 */

const LABEL = 'text-[13px] font-medium text-zinc-900 dark:text-zinc-100';
const FIELD = 'h-11 rounded-xl text-[15px]';
// iOS WebKit gives a date input an intrinsic width and height that ignore `w-full` and `h-11`.
const DATE_FIELD = `${FIELD} min-w-0 appearance-none [&::-webkit-date-and-time-value]:text-left`;

/**
 * Today as the phone's own calendar day. `toISOString()` would answer with the UTC day, which is
 * yesterday for anyone west of Greenwich after midnight.
 *
 * This is the picker's floor, an affordance — **the rule is the server's `CURRENT_DATE`**. At a
 * midnight boundary the two can disagree by a day, and what the technician then sees is the
 * server's refusal, which is written for exactly that.
 */
const localToday = (): string => {
  const now = new Date();
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * The picker's ceiling, three years out. Same nature as the floor: an affordance, not the rule —
 * the boundary caps a period at 731 days. It exists because a phone's year spinner turns
 * `2026` into `9026` in one mis-scroll, and the ceiling is what stops the spinner going there.
 */
const pickerCeiling = (): string => `${new Date().getFullYear() + 3}-12-31`;

/** What `declareUnavailabilityBodySchema` allows, inclusive — kept in step with it by name. */
const MAX_PERIOD_DAYS = 731;
const DAY_MS = 86_400_000;

/**
 * The last day the period may end on. The three-year ceiling above is a spinner guard; this is the
 * server's actual rule, and without it the form happily enabled Save for a span the API then
 * refused with a generic validation error the technician could do nothing about.
 */
const lastAllowedEnd = (start: string, ceiling: string): string => {
  const last = new Date(Date.parse(`${start}T00:00:00Z`) + (MAX_PERIOD_DAYS - 1) * DAY_MS)
    .toISOString()
    .slice(0, 10);
  return last < ceiling ? last : ceiling;
};

interface DeclareUnavailabilityFormProps {
  /** Whether the surface hosting it is showing it — a fresh view is a fresh declaration. */
  open: boolean;
  /** "Cancel": leave the declaration without writing anything. */
  onClose: () => void;
  /**
   * Where the confirmation leads. The profile sheet closes; the ticket screen's sheet goes back
   * to its list, where the period just written is already visible — closing there would drop the
   * technician on the ticket list with nothing to confirm what he did.
   */
  doneLabel: string;
  onDone: () => void;
}

const DeclareUnavailabilityForm = ({
  open,
  onClose,
  doneLabel,
  onDone,
}: DeclareUnavailabilityFormProps) => {
  const { t } = useTranslation();
  const declare = useDeclareUnavailability();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState<{ startDate: string; endDate: string } | null>(null);

  // A reopened form is a new declaration, never the previous one's confirmation or its error.
  useEffect(() => {
    if (open) {
      setStartDate('');
      setEndDate('');
      setReason('');
      setSaved(null);
      declare.reset();
    }
    // `declare` is a fresh object on every render, so it cannot be a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const today = localToday();
  const ceiling = pickerCeiling();
  // The two dates only. The server owns the past and the overlap: one clock, one set of rows.
  const endCeiling = startDate ? lastAllowedEnd(startDate, ceiling) : ceiling;
  const invalid = !startDate || !endDate || endDate < startDate || endDate > endCeiling;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // `isPending` and not only the disabled button: a double tap lands two `submit` events before
    // React has re-rendered the first one's disabled state, and the POST is not idempotent.
    if (invalid || declare.isPending) return;
    declare.mutate(
      { startDate, endDate, reason: reason.trim() || null },
      { onSuccess: () => setSaved({ startDate, endDate }) }
    );
  };

  if (saved) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-5 pb-8 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
          <Check className="h-6 w-6" aria-hidden="true" />
        </span>
        <p className="mt-3 text-[15px] font-semibold text-zinc-900 dark:text-zinc-50">
          {t('Your unavailability has been recorded.')}
        </p>
        <p className="mt-2 font-mono text-[13px] font-semibold text-zinc-900 dark:text-zinc-50">
          {formatPeriod(saved.startDate, saved.endDate)}
        </p>
        <p className="mt-2 max-w-[280px] text-[13px] leading-relaxed text-zinc-500 dark:text-zinc-400">
          {t('You will not be proposed for new interventions during this period.')}
        </p>
        <Button className="mt-5 h-11 w-full" onClick={onDone}>
          {doneLabel}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="unavailability-start" className={LABEL}>
            {t('I am unavailable from')} <span className="text-rose-600">*</span>
          </label>
          {/* The phone's native picker, no calendar library: it already answers YYYY-MM-DD,
              which is exactly what the API takes. */}
          <Input
            id="unavailability-start"
            type="date"
            value={startDate}
            min={today}
            max={ceiling}
            onChange={(event) => setStartDate(event.target.value)}
            className={DATE_FIELD}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="unavailability-end" className={LABEL}>
            {t('to (date range)')} <span className="text-rose-600">*</span>
          </label>
          <Input
            id="unavailability-end"
            type="date"
            value={endDate}
            min={startDate || today}
            max={endCeiling}
            onChange={(event) => setEndDate(event.target.value)}
            className={DATE_FIELD}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="unavailability-reason" className={LABEL}>
            {t('Reason')}{' '}
            <span className="font-normal text-zinc-400 dark:text-zinc-500">({t('optional')})</span>
          </label>
          <Input
            id="unavailability-reason"
            value={reason}
            maxLength={500}
            placeholder={t('e.g. Time off')}
            onChange={(event) => setReason(event.target.value)}
            className={FIELD}
          />
        </div>

        {declare.error && (
          <p className="text-[13px] leading-snug text-rose-600 dark:text-rose-400" role="alert">
            {apiErrorMessage(t, declare.error)}
          </p>
        )}

        {/* The reassurance the design asks for, in its own words: the account stays active. */}
        <p className="flex gap-2.5 rounded-xl border border-zinc-200 p-3 text-[12.5px] leading-relaxed text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden="true" />
          {t(
            'Your account stays active. You simply will not be proposed for an intervention covering this period, and you become eligible again automatically at the end.'
          )}
        </p>
      </div>

      {/* Outside the scroll region: the two actions stay on screen whatever the sheet's height. */}
      <div className="flex shrink-0 gap-2.5 border-t border-zinc-200 px-5 pb-8 pt-4 dark:border-zinc-800">
        <Button type="button" variant="outline" className="h-11 px-5" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button type="submit" className="h-11 flex-1" disabled={invalid || declare.isPending}>
          {t('Save')}
        </Button>
      </div>
    </form>
  );
};

export default DeclareUnavailabilityForm;
